import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useOktaAuth } from "@okta/okta-react";
import CommunicationPreferencesService from "../../../CommunicationPreferencesService";
import {
  DeliveryHistoryFilters,
  SaveDefaultsPayload,
  SaveOrganizationPreferencePayload,
} from "../../../models/communication/communicationPreferences.model";
import useProfile from "../useProfile";

function useAccessToken(): string | undefined {
  const { authState } = useOktaAuth();
  return authState?.accessToken?.accessToken;
}

export function useCommunicationPreferencesOverview() {
  const token = useAccessToken();

  return useQuery({
    queryKey: ["communicationPreferencesOverview"],
    queryFn: () => CommunicationPreferencesService.getOverview(token!),
    enabled: !!token,
    refetchOnWindowFocus: false,
    retry: false,
  });
}

export function useSaveCommunicationDefaults() {
  const token = useAccessToken();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      payload,
      skipInvalidate = false,
    }: {
      payload: SaveDefaultsPayload;
      skipInvalidate?: boolean;
    }) => {
      if (!token) throw new Error("Not authenticated");
      return CommunicationPreferencesService.saveDefaults(token, payload).then(
        (data) => ({ data, skipInvalidate })
      );
    },
    onSuccess: (result) => {
      if (result.skipInvalidate) return;
      queryClient.invalidateQueries({
        queryKey: ["communicationPreferencesOverview"],
      });
    },
  });
}

export function useOrganizationPreference(organizationId?: number | string | null) {
  const token = useAccessToken();

  return useQuery({
    queryKey: ["organizationPreference", organizationId],
    queryFn: () =>
      CommunicationPreferencesService.getOrganization(token!, organizationId!),
    enabled: !!token && !!organizationId,
    refetchOnWindowFocus: false,
    retry: false,
  });
}

export function useSaveOrganizationPreference() {
  const token = useAccessToken();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      organizationId,
      payload,
    }: {
      organizationId: number | string;
      payload: SaveOrganizationPreferencePayload;
    }) => {
      if (!token) throw new Error("Not authenticated");
      return CommunicationPreferencesService.saveOrganization(
        token,
        organizationId,
        payload
      );
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["communicationPreferencesOverview"],
      });
      queryClient.invalidateQueries({
        queryKey: ["organizationPreference", variables.organizationId],
      });
    },
  });
}

export function useRestoreOrganizationDefaults() {
  const token = useAccessToken();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (organizationId: number | string) => {
      if (!token) throw new Error("Not authenticated");
      return CommunicationPreferencesService.restoreOrganizationDefaults(
        token,
        organizationId
      );
    },
    onSuccess: (_data, organizationId) => {
      queryClient.invalidateQueries({
        queryKey: ["communicationPreferencesOverview"],
      });
      queryClient.invalidateQueries({
        queryKey: ["organizationPreference", organizationId],
      });
    },
  });
}

export function useSetExternalNotifications() {
  const token = useAccessToken();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      organizationId,
      enabled,
    }: {
      organizationId: number | string;
      enabled: boolean;
      skipInvalidate?: boolean;
    }) => {
      if (!token) throw new Error("Not authenticated");
      return CommunicationPreferencesService.setExternalNotifications(
        token,
        organizationId,
        enabled
      );
    },
    onSuccess: (_data, variables) => {
      if (variables.skipInvalidate) return;
      queryClient.invalidateQueries({
        queryKey: ["communicationPreferencesOverview"],
      });
      queryClient.invalidateQueries({
        queryKey: ["organizationPreference", variables.organizationId],
      });
    },
  });
}

export function useDeliveryHistory(
  filters: DeliveryHistoryFilters = {},
  enabled = true
) {
  const token = useAccessToken();

  return useQuery({
    queryKey: ["deliveryHistory", filters],
    queryFn: () =>
      CommunicationPreferencesService.getDeliveryHistory(token!, filters),
    enabled: !!token && enabled,
    refetchOnWindowFocus: false,
    retry: false,
  });
}

export function useDeliveryHistoryPreview(id?: number | string | null) {
  const token = useAccessToken();

  return useQuery({
    queryKey: ["deliveryHistoryPreview", id],
    queryFn: () =>
      CommunicationPreferencesService.getDeliveryHistoryItem(token!, id!),
    enabled: !!token && id !== null && id !== undefined && id !== "",
    refetchOnWindowFocus: false,
    retry: false,
  });
}

export function useCommunicationSetupStatus() {
  const { data: profile } = useProfile();
  const userId = profile?.user?.id;

  return useQuery({
    queryKey: ["communicationSetupStatus", userId],
    queryFn: () => CommunicationPreferencesService.getSetupStatus(userId!),
    enabled: !!userId,
  });
}

export function useMarkCommunicationSetupComplete() {
  const queryClient = useQueryClient();
  const { data: profile } = useProfile();
  const userId = profile?.user?.id;

  return useMutation({
    mutationFn: async () => {
      if (!userId) throw new Error("User not found");
      CommunicationPreferencesService.markSetupComplete(userId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["communicationSetupStatus", userId],
      });
    },
  });
}

export function useCsPreferencesUpdateNotice() {
  const { data: profile } = useProfile();
  const userId = profile?.user?.id;

  return useQuery({
    queryKey: ["csPreferencesUpdateNotice", userId],
    queryFn: () => CommunicationPreferencesService.getCsUpdateNotice(userId!),
    enabled: !!userId,
  });
}

export function useDismissCsPreferencesNotice() {
  const queryClient = useQueryClient();
  const { data: profile } = useProfile();
  const userId = profile?.user?.id;

  return useMutation({
    mutationFn: async () => {
      if (!userId) throw new Error("User not found");
      CommunicationPreferencesService.dismissCsUpdateNotice(userId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["csPreferencesUpdateNotice", userId],
      });
    },
  });
}

/** @deprecated Use useCommunicationPreferencesOverview */
export function useCommunicationPreferences() {
  return useCommunicationPreferencesOverview();
}

/** @deprecated Use useSaveCommunicationDefaults */
export function useSaveCommunicationPreferences() {
  return useSaveCommunicationDefaults();
}
